import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    selectedCategoryIds = "selectedCategoryIds",
    headingBackgroundColor = "headingBackgroundColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.headingBackgroundColor]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const getCategoriesParameter = {
        // maxDepth: props.fields.showOnlyTopLevelCategories ? 1 : 2,
        includeStartCategory: false,
    };
    const categoryListPageLink = getPageLinkByPageType(state, "CategoryListPage");
    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
        categoryListPageUrl: categoryListPageLink?.url,
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface CENCategoryListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    categoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    mainContainer?: InjectableCss;
    categoryImageLink?: LinkPresentationProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkPresentationProps;
    categoryName?: TypographyProps;
    titleText?: TypographyProps;
    titleSection?: InjectableCss;
    subCategoryNameLink?: LinkPresentationProps;
    categoryLink?: InjectableCss;
    categoryLinkMobile?: InjectableCss;
    gradientContainer?: InjectableCss;
}

export const CENcategoryListStyles: CENCategoryListStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    mainContainer: {
        css: css`
            padding: 32px;
            border: 4px solid ${COLORS.background.grayShade};
            margin-bottom: 100px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 24px;
                        margin-bottom: 56px;
                    `,
                    css`
                        padding: 24px;
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-bottom: 74px;
                        padding: 16px;
                    `,
                    null,
                ]);
            }}
        `,
    },
    titleSection: {
        css: css`
            display: flex;
            max-width: 100%;
            flex-basis: 100%;
            padding: 0;
            justify-content: space-between;
            margin-bottom: 32px !important;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: block;
                            margin-bottom: 24px !important;
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 20,
        css: css`
            height: 175px;
            overflow: auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            height: auto;
                            overflow: hidden;
                        `,
                        null,
                    ],
                    "max",
                );
            }}
            > div[class^="GridOffset"] {
                width: 100%;
                margin: 0;
                > div[class^="GridItemStyle"] {
                    padding: 0;
                    @media (min-width: 768px) {
                        flex-basis: 50% !important;
                        max-width: 50% !important;
                        &:nth-child(2n + 1) {
                            padding-right: 8px;
                        }
                    }

                    ${({ theme }) => {
                        return breakpointMediaQueries(theme, [
                            null,
                            css`
                                flex-basis: 100%;
                                max-width: 100%;
                            `,
                            null,
                            null,
                        ]);
                    }}
                }
            }
        `,
    },
    titleText: {
        variant: "h5",
        transform: "inherit",
        css: css`
            border-width: 20px;
            text-transform: uppercase;
            font-size: ${TYPOGRAPHY.size.large} !important;
            letter-spacing: 0em;
            margin: 0;
        `,
    },
    categoryItem: {
        // width is dynamic here
        width: 0,
    },
    innerContainer: {
        gap: 10,
        css: css`
            > div[class^="GridOffset"] {
                width: 100%;
                margin: 0;
            }
        `,
    },
    categoryNameLinkItem: {
        width: [6, 6, 12, 12, 12],
        css: css`
            padding: 0;
            margin-bottom: 16px !important;
            flex-basis: 50%;
            max-width: 50%;
        `,
    },
    categoryNameLink: {
        css: css`
            &:hover {
                text-decoration: underline;
            }
        `,
    },
    categoryName: {
        css: css`
            color: ${getProp("theme.typography.p.color")};
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        font-size: ${TYPOGRAPHY.size.smaller};
                    `,
                    null,
                ]);
            }}
        `,
    },
    categoryLink: {
        css: css`
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: 16px;
            color: ${getColor("link.base")};
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    categoryLinkMobile: {
        css: css`
            display: none;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: block;
                            font-weight: ${TYPOGRAPHY.weight.mediumbold};
                            font-size: ${TYPOGRAPHY.size.smaller};
                            line-height: 16px;
                            color: ${getColor("link.base")};
                        `,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },
    gradientContainer: {
        css: css`
            box-shadow: 0px 4px 8px rgba(38, 37, 37, 0.24);
            height: 16px;
        `,
    },
};

const styles = CENcategoryListStyles;

class CategoryList extends React.Component<Props> {
    container = React.createRef<HTMLDivElement>();
    categoryItemStyles: GridItemProps = { ...styles.categoryItem };

    UNSAFE_componentWillMount(): void {
        const { categoriesDataView, getCategoriesParameter, loadCategories } = this.props;
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }

    recalculateWidth = () => {
        if (!this.container.current) {
            return;
        }

        const { breakpoints } = this.props.theme;
        const { clientWidth } = this.container.current;
        let width: GridWidths;
        if (clientWidth < breakpoints.values[1]) {
            width = 12;
        } else if (clientWidth < breakpoints.values[2]) {
            width = 6;
        } else if (clientWidth < breakpoints.values[3]) {
            width = 4;
        } else {
            width = 2;
        }

        if (width === this.categoryItemStyles.width) {
            return;
        }

        this.categoryItemStyles.width = width;
        this.forceUpdate();
    };

    componentDidMount() {
        this.recalculateWidth();
        window.addEventListener("resize", this.recalculateWidth, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.recalculateWidth);
    }

    componentDidUpdate(prevProps: Props) {
        if (!prevProps.categoriesDataView.value && this.props.categoriesDataView.value) {
            this.recalculateWidth();
        }
    }

    render() {
        const {
            categoriesDataView,
            topLevelCategoryIds,
            categoryListPageUrl,
            fields: { title },
        } = this.props;
        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value || !topLevelCategoryIds) {
            return null;
        }

        const categories = categoriesDataView.value.filter(o => topLevelCategoryIds.indexOf(o.id) > -1);
        const selectedCatIds = this.props.fields.selectedCategoryIds;
        const allCategories = categoriesDataView.value;
        const selectedCategories = allCategories?.filter(el => {
            return selectedCatIds?.some(f => {
                return f === el.id;
            });
        });

        return (
            <StyledWrapper>
                <StyledWrapper
                    {...styles.gradientContainer}
                    style={{ background: fields.headingBackgroundColor }}
                ></StyledWrapper>
                <StyledWrapper ref={this.container} {...styles.mainContainer}>
                    <GridItem {...styles.titleSection}>
                        {title && (
                            <Typography
                                variant="headerSecondary"
                                {...styles.titleText}
                                data-test-selector="categoryListTitle"
                            >
                                {this.props.fields.title}
                            </Typography>
                        )}
                        <Link href={categoryListPageUrl} {...styles.categoryLink}>
                            {translate("Shop all Categories")}
                            <span> &#8594; </span>
                        </Link>
                    </GridItem>
                    <GridContainer {...styles.container}>
                        {selectedCategories.map(category => (
                            <GridItem key={category.id.toString()} {...this.categoryItemStyles}>
                                <GridContainer {...styles.innerContainer}>
                                    <GridItem
                                        {...styles.categoryNameLinkItem}
                                        data-test-selector="categoryNameLinkItem"
                                    >
                                        <Link href={category.path} {...styles.categoryNameLink}>
                                            <Typography {...styles.categoryName}>
                                                {category.shortDescription}
                                            </Typography>
                                        </Link>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        ))}
                        <Link href={categoryListPageUrl} {...styles.categoryLinkMobile}>
                            {translate("Shop all Categories")}
                            <span> &#8594; </span>
                        </Link>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const SubCategoryLinkView = ({ category }: ReturnType<typeof linkMapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link href={category.path} {...styles.subCategoryNameLink}>
            {category.shortDescription}
        </Link>
    );
};

const linkMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(linkMapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CategoryList)),
    definition: {
        group: "Categories",
        icon: "list-ul",
        allowedContexts: ["CategoryListPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.headingBackgroundColor,
                displayName: "Background Color",
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "linear-gradient(90deg, #266b40 27.08%, #2d804c 71.87%, #43687a 100%)",
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 3,
            },
        ],
    },
};

export default widgetModule;
